import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';
import usePermissionsStore from '../stores/permissionsStore';

function buildSuperadminItems(groupId, T) {
  const gid = groupId || 1;
  return [
    { to: '/', label: T.nav.dashboard, icon: 'home' },
    { to: '/groups', label: T.nav.groups, icon: 'groups' },
    { to: '/members', label: T.nav.members, icon: 'users' },
    { to: '/transactions', label: T.nav.transactions, icon: 'wallet' },
    { to: '/calendar', label: T.nav.calendar, icon: 'calendar' },
    { to: '/transactions/import', label: T.nav.import, icon: 'download' },
    { to: '/fines', label: T.nav.fines, icon: 'alert' },
    { to: '/changiwa', label: T.nav.changiwa, icon: 'sparkles' },
    { to: '/users', label: T.nav.users, icon: 'key' },
    { to: '/permissions', label: T.nav.permissions, icon: 'lock' },
    { to: `/groups/${gid}/expenses`, label: T.nav.expenses, icon: 'expenses' },
    { to: '/audit-log', label: T.nav.auditLog, icon: 'shield' },
    { to: '/sms-logs', label: T.nav.smsLogs, icon: 'sms' },
    { to: '/group-requests', label: T.nav.groupRequests, icon: 'request' },
    { to: '/system-settings', label: T.nav.systemSettings || 'Mipangilio', icon: 'settings' },
  ];
}

const LEADER_ROLES = ['mwenyekiti', 'katibu', 'mwekahazina'];

function buildMemberItems(groupId, T, perms, memberRole) {
  const gid = groupId || '';
  const p = perms || {};
  const isLeader = LEADER_ROLES.includes(memberRole);
  return [
    { to: '/', label: T.nav.dashboard, icon: 'home' },
    ...(p.transactions !== false ? [{ to: `/transactions?group_id=${gid}`, label: T.nav.transactions, icon: 'wallet' }] : []),
    ...(gid && p.fines !== false          ? [{ to: `/fines?group_id=${gid}`,      label: T.nav.fines,        icon: 'alert'    }] : []),
    ...(gid && p.monthly_report !== false ? [{ to: `/groups/${gid}/monthly`,      label: T.nav.report,       icon: 'calendar' }] : []),
    ...(gid && p.changiwa !== false       ? [{ to: `/changiwa?group_id=${gid}`,   label: T.nav.changiwa,     icon: 'sparkles' }] : []),
    ...(gid && p.distribution !== false && p.dissolve_at_end !== false ? [{ to: `/groups/${gid}/distribution`, label: T.nav.distribution, icon: 'chart' }] : []),
    ...(gid && isLeader ? [{ to: `/transactions/import?group_id=${gid}`, label: T.nav.import, icon: 'download' }] : []),
  ];
}

function buildAdminItems(groupId, T, perms) {
  const gid = groupId || '';
  const p   = perms || {};

  return [
    { to: '/', label: T.nav.dashboard, icon: 'home' },
    { to: `/members?group_id=${gid}`, label: T.nav.members, icon: 'users' },
    { to: `/transactions?group_id=${gid}`, label: T.nav.transactions, icon: 'wallet' },
    { to: `/fines?group_id=${gid}`, label: T.nav.fines, icon: 'alert' },
    { to: `/groups/${gid}/monthly`, label: T.nav.report, icon: 'calendar' },
    { to: `/changiwa?group_id=${gid}`, label: T.nav.changiwa, icon: 'sparkles' },
    { to: `/groups/${gid}/settings`, label: T.nav.settings, icon: 'settings' },
    { to: `/transactions/import?group_id=${gid}`, label: T.nav.import, icon: 'download' },
    ...(p.dissolve_at_end !== false ? [{ to: `/groups/${gid}/distribution`, label: T.nav.distribution, icon: 'chart' }] : []),
    { to: `/groups/${gid}/expenses`, label: T.nav.expenses, icon: 'expenses' },
    { to: `/groups/${gid}/notify`, label: T.nav.notifications || 'Arifa', icon: 'bell' },
  ];
}

function iconPaths(name) {
  const icons = {
    home: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 10.5 12 3l9 7.5M5.25 9.75V20h13.5V9.75" />,
    groups: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 7.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15.75 8.25a2.25 2.25 0 1 1 0 4.5M3.75 19.5a5.25 5.25 0 0 1 10.5 0M14.25 19.5a4.5 4.5 0 0 1 4.5-4.5 4.5 4.5 0 0 1 1.5.255" />
    </>,
    users: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 12a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M4.5 20.25a7.5 7.5 0 0 1 15 0" />
    </>,
    wallet: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3.75 7.5h16.5v9.75a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V7.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3.75 9.75h12.75a1.5 1.5 0 0 0 0-3H6A2.25 2.25 0 0 1 8.25 4.5h8.25" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M16.5 13.125h.75" />
    </>,
    calendar: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M6.75 2.25v3M17.25 2.25v3M3 8.25h18M4.5 5.25h15A1.5 1.5 0 0 1 21 6.75v12A1.5 1.5 0 0 1 19.5 20.25h-15A1.5 1.5 0 0 1 3 18.75v-12a1.5 1.5 0 0 1 1.5-1.5Z" />
    </>,
    download: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 3.75v10.5" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m8.25 10.5 3.75 3.75 3.75-3.75" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M4.5 19.5h15" />
    </>,
    alert: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m12 4.5 8.25 14.25H3.75L12 4.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 9.75v3.75M12 16.5h.008v.008H12V16.5Z" />
    </>,
    sparkles: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m12 3 1.35 4.65L18 9l-4.65 1.35L12 15l-1.35-4.65L6 9l4.65-1.35L12 3Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m19.5 15 .675 2.325L22.5 18l-2.325.675L19.5 21l-.675-2.325L16.5 18l2.325-.675L19.5 15ZM4.5 14.25l.9 3.1 3.1.9-3.1.9-.9 3.1-.9-3.1-3.1-.9 3.1-.9.9-3.1Z" />
    </>,
    key: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15.75 8.25a4.5 4.5 0 1 1-8.193 2.58L3 15.386V18h2.614l1.307-1.307V15.386h1.307l1.418-1.418a4.5 4.5 0 0 1 6.104-5.718Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M16.5 9h.008v.008H16.5V9Z" />
    </>,
    settings: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10.325 4.317a1.724 1.724 0 0 1 3.35 0 1.724 1.724 0 0 0 2.574 1.066 1.724 1.724 0 0 1 2.37 2.37 1.724 1.724 0 0 0 1.065 2.574 1.724 1.724 0 0 1 0 3.35 1.724 1.724 0 0 0-1.066 2.574 1.724 1.724 0 0 1-2.37 2.37 1.724 1.724 0 0 0-2.574 1.065 1.724 1.724 0 0 1-3.35 0 1.724 1.724 0 0 0-2.574-1.066 1.724 1.724 0 0 1-2.37-2.37 1.724 1.724 0 0 0-1.065-2.574 1.724 1.724 0 0 1 0-3.35 1.724 1.724 0 0 0 1.066-2.574 1.724 1.724 0 0 1 2.37-2.37 1.724 1.724 0 0 0 2.574-1.065Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z" />
    </>,
    chart: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M4.5 19.5h15" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M7.5 16.5v-6M12 16.5V9M16.5 16.5v-9" />
    </>,
    more: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.25 12h.008v.008H5.25V12Zm6.742 0H12v.008h-.008V12Zm6.75 0h.008v.008h-.008V12Z" />
    </>,
    expenses: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12h6m-6 4h6M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 8h6" />
    </>,
    lock: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3.75 10.5h16.5v10.5a.75.75 0 0 1-.75.75H4.5a.75.75 0 0 1-.75-.75V10.5Z" />
      <circle cx="12" cy="15.75" r="1.5" fill="currentColor" />
    </>,
    shield: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 3 4.5 6.75v5.25c0 4.478 3.112 8.676 7.5 9.75 4.388-1.074 7.5-5.272 7.5-9.75V6.75L12 3Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m9 12 2 2 4-4" />
    </>,
    request: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12h6M9 15h4" />
    </>,
    sms: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227.857.127 1.722.224 2.591.291.273.021.524.159.679.379l1.91 2.671c.404.566 1.252.566 1.656 0l1.91-2.671a.836.836 0 0 1 .679-.379 41.182 41.182 0 0 0 2.591-.291c1.584-.233 2.708-1.626 2.708-3.226V6.741c0-1.6-1.124-2.994-2.708-3.227a41.05 41.05 0 0 0-13.443 0c-1.584.233-2.708 1.626-2.708 3.227v6.018Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M7.5 8.25h9M7.5 11.25h5.25" />
    </>,
    bell: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
    </>,
    logout: <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15.75 3.75h2.25a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5h-2.25" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10.5 16.5 15 12l-4.5-4.5M15 12H3.75" />
    </>,
  };

  return icons[name] || icons.home;
}

function AppIcon({ name, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className} aria-hidden="true">
      {iconPaths(name)}
    </svg>
  );
}

function isPathActive(itemTo, currentPath) {
  const [itemPath] = itemTo.split('?');
  if (itemPath === '/' && currentPath === '/') return true;
  if (itemPath === '/') return false;
  if (currentPath === itemPath) return true;
  if (currentPath.startsWith(itemPath + '/')) return true;
  return false;
}

export default function Sidebar({ open, onOpen, onClose }) {
  const { user, logout } = useAuthStore();
  const { T } = useLang();
  const permissions = usePermissionsStore(s => s.permissions);
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const role = user?.role || 'member';
  const roleLabel = { superadmin: T.role.superadmin, admin: T.role.admin, member: T.role.member };

  let items;
  if (role === 'superadmin') {
    items = buildSuperadminItems(user?.group_id, T);
  } else if (role === 'admin') {
    items = buildAdminItems(user?.group_id, T, permissions);
  } else {
    items = buildMemberItems(user?.group_id, T, permissions, user?.member_role);
  }

  const primaryMobileItems = items.slice(0, 4);
  const secondaryMobileItems = items.slice(4);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 bg-slate-950/45 backdrop-blur-sm lg:hidden" onClick={onClose} />
      )}

      <aside className={`fixed inset-y-4 left-4 z-40 hidden w-72 overflow-hidden rounded-[28px] border border-white/45 bg-[linear-gradient(180deg,rgba(15,118,110,0.96),rgba(15,23,42,0.96))] text-white shadow-[0_28px_80px_rgba(15,23,42,0.28)] backdrop-blur xl:block ${
        open ? 'translate-x-0' : 'translate-x-0'
      }`}>
        <div className="flex h-full flex-col">
          <div className="border-b border-white/10 px-6 pb-5 pt-6">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/14 shadow-inner shadow-white/10">
                <span className="text-xl">💚</span>
              </div>
              <div>
                <p className="text-lg font-semibold tracking-wide">Vikundi Kiganjani</p>
                <p className="text-xs text-emerald-100/70">{T.sidebar.tagline}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <p className="truncate text-sm font-semibold">{user?.name || T.common.user}</p>
              <p className="mt-1 text-xs text-emerald-100/75">{roleLabel[role] || role}</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-5">
            {items.map((item) => {
              const active = isPathActive(item.to, location.pathname);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`group flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all ${
                    active
                      ? 'bg-white text-slate-900 shadow-[0_12px_30px_rgba(255,255,255,0.22)]'
                      : 'text-white/78 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                    active ? 'bg-emerald-100 text-emerald-700' : 'bg-white/10 text-white/80'
                  }`}>
                    <AppIcon name={item.icon} className="h-5 w-5" />
                  </span>
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="border-t border-white/10 p-4">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-2xl bg-white/8 px-4 py-3 text-sm font-medium text-white/85 transition hover:bg-white/14"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
                <AppIcon name="logout" className="h-5 w-5" />
              </span>
              {T.nav.logout}
            </button>
          </div>
        </div>
      </aside>

      <aside className={`fixed inset-y-0 left-0 z-40 w-[86vw] max-w-sm transform border-r border-white/45 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(236,253,245,0.96))] text-slate-900 shadow-[0_24px_80px_rgba(15,23,42,0.24)] backdrop-blur transition-transform duration-300 lg:hidden ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col">
          <div className="border-b border-slate-200/70 px-5 pb-5 pt-6">
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#14b8a6,#f59e0b)] text-lg text-white shadow-lg">
                  💚
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-900">Vikundi Kiganjani</p>
                  <p className="text-xs text-slate-500">{T.sidebar.quickMenu}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-2xl bg-slate-100 p-3 text-slate-500 transition hover:bg-slate-200"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
            <div className="rounded-[24px] bg-[linear-gradient(135deg,rgba(20,184,166,0.12),rgba(249,115,22,0.12))] p-4">
              <p className="truncate text-sm font-semibold text-slate-900">{user?.name || T.common.user}</p>
              <p className="mt-1 text-xs text-slate-600">{roleLabel[role] || role}</p>
            </div>
          </div>

          <nav className="flex-1 space-y-2 overflow-y-auto px-4 py-5">
            {items.map((item) => {
              const active = isPathActive(item.to, location.pathname);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all ${
                    active
                      ? 'bg-slate-900 text-white shadow-[0_14px_35px_rgba(15,23,42,0.2)]'
                      : 'bg-white/70 text-slate-700 hover:bg-white'
                  }`}
                >
                  <span className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                    active ? 'bg-white/10' : 'bg-slate-100 text-teal-700'
                  }`}>
                    <AppIcon name={item.icon} className="h-5 w-5" />
                  </span>
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="border-t border-slate-200/70 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 transition hover:bg-rose-100"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-100">
                <AppIcon name="logout" className="h-5 w-5" />
              </span>
              {T.nav.logout}
            </button>
          </div>
        </div>
      </aside>

      {/* More menu grid popup */}
      {showMoreMenu && (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          onClick={() => setShowMoreMenu(false)}
        />
      )}
      {showMoreMenu && (
        <div className="fixed inset-x-3 z-40 lg:hidden" style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom))' }}>
          <div
            className="mx-auto max-w-md overflow-hidden rounded-[28px] shadow-[0_16px_50px_rgba(6,78,59,0.40)]"
            style={{ background: 'linear-gradient(160deg,#064e3b 0%,#065f46 60%,#047857 100%)' }}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-200">{T.sidebar.moreMenu}</p>
              <button
                onClick={() => setShowMoreMenu(false)}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 p-3">
              {secondaryMobileItems.map((item) => {
                const active = isPathActive(item.to, location.pathname);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setShowMoreMenu(false)}
                    className={`flex flex-col items-center gap-2 rounded-[20px] px-2 py-3.5 text-[11px] font-bold transition-all ${
                      active
                        ? 'bg-white text-emerald-700 shadow-[0_4px_18px_rgba(255,255,255,0.3)]'
                        : 'bg-white/10 text-emerald-100 hover:bg-white/20'
                    }`}
                  >
                    <AppIcon name={item.icon} className="h-5 w-5" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                );
              })}
              <button
                onClick={() => { setShowMoreMenu(false); handleLogout(); }}
                className="flex flex-col items-center gap-2 rounded-[20px] bg-rose-500/20 px-2 py-3.5 text-[11px] font-bold text-rose-200 transition-all hover:bg-rose-500/30"
              >
                <AppIcon name="logout" className="h-5 w-5" />
                <span>{T.nav.logout}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom nav bar */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-3 pb-[calc(0.6rem+env(safe-area-inset-bottom))] pt-3 lg:hidden">
        <div
          className="pointer-events-auto mx-auto flex max-w-md items-center justify-between rounded-[32px] px-2 py-2 shadow-[0_8px_40px_rgba(6,78,59,0.45)]"
          style={{ background: 'linear-gradient(135deg,#064e3b 0%,#065f46 55%,#047857 100%)' }}
        >
          {primaryMobileItems.map((item) => {
            const active = isPathActive(item.to, location.pathname);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setShowMoreMenu(false)}
                className={`flex min-w-0 flex-1 flex-col items-center gap-1 rounded-[24px] px-2 py-2 text-[10px] font-bold transition-all duration-200 ${
                  active
                    ? 'bg-white text-emerald-700 shadow-[0_4px_18px_rgba(255,255,255,0.35)]'
                    : 'text-emerald-100/80 active:scale-95'
                }`}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-full transition-all ${
                  active ? 'bg-emerald-100' : ''
                }`}>
                  <AppIcon name={item.icon} className="h-4 w-4" />
                </span>
                <span className="truncate leading-tight">{item.label}</span>
              </NavLink>
            );
          })}

          {secondaryMobileItems.length > 0 ? (
            <button
              onClick={() => setShowMoreMenu(m => !m)}
              className={`flex min-w-0 flex-1 flex-col items-center gap-1 rounded-[24px] px-2 py-2 text-[10px] font-bold transition-all duration-200 ${
                showMoreMenu
                  ? 'bg-white text-emerald-700 shadow-[0_4px_18px_rgba(255,255,255,0.35)]'
                  : 'text-emerald-100/80 active:scale-95'
              }`}
            >
              <span className={`flex h-7 w-7 items-center justify-center rounded-full transition-all ${
                showMoreMenu ? 'bg-emerald-100' : ''
              }`}>
                <AppIcon name="more" className="h-4 w-4" />
              </span>
              <span className="truncate leading-tight">{T.nav.more}</span>
            </button>
          ) : (
            <button
              onClick={open ? onClose : onOpen}
              className={`flex min-w-0 flex-1 flex-col items-center gap-1 rounded-[24px] px-2 py-2 text-[10px] font-bold transition-all duration-200 ${
                open
                  ? 'bg-white text-emerald-700 shadow-[0_4px_18px_rgba(255,255,255,0.35)]'
                  : 'text-emerald-100/80 active:scale-95'
              }`}
            >
              <span className={`flex h-7 w-7 items-center justify-center rounded-full ${open ? 'bg-emerald-100' : ''}`}>
                <AppIcon name="more" className="h-4 w-4" />
              </span>
              <span className="truncate leading-tight">{T.nav.menu}</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
